import { type FormEvent, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { adminApiClient } from "../../lib/adminApiClient";
import { queryKeys } from "../../lib/queryClient";
import { TicketStatusBadge, TicketPriorityBadge } from "../../components/StatusBadge";
import { ErrorBanner, LoadingState, describeError } from "../../components/States";
import { formatDateTime } from "../../lib/format";

const STATUSES = ["NEW", "PENDING", "RESOLVED", "CANCELLED"] as const;
const PRIORITIES = ["CRITICAL", "HIGH", "NORMAL", "LOW"] as const;

export function AdminTicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [body, setBody] = useState("");

  const { data: ticket, isLoading, isError, error, refetch } = useQuery({
    queryKey: queryKeys.adminTicket(id ?? ""),
    queryFn: () => adminApiClient.getTicket(id as string),
    enabled: Boolean(id),
  });

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: queryKeys.adminTicket(id ?? "") });
    void queryClient.invalidateQueries({ queryKey: queryKeys.adminTickets("") });
  }

  const reply = useMutation({
    mutationFn: (text: string) => adminApiClient.addTicketMessage(id as string, { body: text }),
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKeys.adminTicket(id ?? ""), updated);
      setBody("");
      invalidate();
    },
  });

  const setStatus = useMutation({
    mutationFn: (status: (typeof STATUSES)[number]) =>
      adminApiClient.setTicketStatus(id as string, { status }),
    onSuccess: invalidate,
  });

  const setPriority = useMutation({
    mutationFn: (priority: (typeof PRIORITIES)[number]) =>
      adminApiClient.setTicketPriority(id as string, { priority }),
    onSuccess: invalidate,
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = body.trim();
    if (!trimmed) return;
    reply.mutate(trimmed);
  }

  if (isLoading) return <LoadingState label="Loading ticket…" />;
  if (isError) return <ErrorBanner error={error} onRetry={() => void refetch()} />;
  if (!ticket) return null;

  return (
    <div className="stack" style={{ maxWidth: 720 }}>
      <div className="row row--between">
        <h1>{ticket.subject}</h1>
        <div className="row">
          <TicketStatusBadge status={ticket.status} />
          <TicketPriorityBadge priority={ticket.priority} />
        </div>
      </div>

      <div className="card stack">
        <div className="row row--between">
          <span className="muted">From</span>
          <span>
            {ticket.firstName} {ticket.lastName}
          </span>
        </div>
        <div className="row row--between">
          <span className="muted">Email</span>
          <span>{ticket.email}</span>
        </div>
        <div className="row row--between">
          <span className="muted">IP address</span>
          <span className="mono text-sm">{ticket.ipAddress ?? "—"}</span>
        </div>
        <div className="row row--between">
          <span className="muted">Location (approximate)</span>
          <span>{ticket.locationLabel ?? "—"}</span>
        </div>
        <div className="row row--between">
          <span className="muted">Preferred contact</span>
          <span>
            {ticket.contactMethod === "PHONE" ? `Phone: ${ticket.contactPhone}` : "Email"}
          </span>
        </div>
        <div className="row row--between">
          <span className="muted">Opened</span>
          <span>{formatDateTime(ticket.createdAt)}</span>
        </div>
      </div>

      <div className="card stack">
        <h2>Manage</h2>
        <div className="row">
          <div className="field" style={{ flex: 1 }}>
            <label htmlFor="ticket-status">Status</label>
            <select
              id="ticket-status"
              value={ticket.status}
              disabled={setStatus.isPending}
              onChange={(e) => setStatus.mutate(e.target.value as (typeof STATUSES)[number])}
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div className="field" style={{ flex: 1 }}>
            <label htmlFor="ticket-priority">Priority</label>
            <select
              id="ticket-priority"
              value={ticket.priority}
              disabled={setPriority.isPending}
              onChange={(e) => setPriority.mutate(e.target.value as (typeof PRIORITIES)[number])}
            >
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div className="message-thread">
          {ticket.messages.map((m) => (
            <div key={m.id} className={`message-bubble ${m.isFromAdmin ? "is-mine" : ""}`}>
              <p className="text-sm muted">
                <strong>{m.authorName}</strong> · {formatDateTime(m.createdAt)}
              </p>
              <p className="text-sm">{m.body}</p>
            </div>
          ))}
        </div>

        <form
          onSubmit={handleSubmit}
          className="row"
          style={{ padding: "var(--space-3) var(--space-4)", borderTop: "1px solid var(--color-border)" }}
        >
          <input
            type="text"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Reply to the customer… (sets status to Pending and emails them)"
            maxLength={5_000}
            style={{ flex: 1 }}
          />
          <button type="submit" className="btn btn-primary" disabled={!body.trim() || reply.isPending}>
            {reply.isPending ? "Sending…" : "Send reply"}
          </button>
        </form>
        {reply.isError && (
          <p
            className="field-error-text"
            role="alert"
            style={{ padding: "0 var(--space-4) var(--space-3)" }}
          >
            {describeError(reply.error)}
          </p>
        )}
      </div>
    </div>
  );
}
